/**
 * Client-profile persistence — talks to OUR backend (/rest/profiles) with the signed-in user's token.
 *
 * Privacy split (matches the server's assume-breach data model, D6):
 *   identity  — who the client is (names, DOB, contact) — stored encrypted in its own table
 *   factFind  — the financial situation the engine models — stored encrypted separately,
 *               with the identity fields BLANKED so the two segments are only joinable server-side.
 * `splitFactFind` / `mergeFactFind` are the single source of truth for that split.
 */
import axios from 'axios';
import { getAccessToken } from './authClient.ts';
import { NUMERIC_FIELDS, normalizeNumericFieldsOn, normalizeNumericFactFindFields } from '../utils/normalizeNumericFields.ts';
import type { DraftFactFind } from '../constants/initialFactFind.ts';
import { apiBaseUrl } from './apiBase.ts';

/**
 * Identity segment — who the client is (server/src/shared/clientProfileRepo.ts's ClientIdentity,
 * plus a couple of completer/partner fields this client also stores in the same encrypted blob).
 */
export interface ClientProfileIdentity {
  fullName: string;
  dateOfBirth?: string;
  email?: string;
  phone?: string;
  partnerFullName?: string;
  partnerDateOfBirth?: string;
  completerFirstName?: string;
  completerLastName?: string;
  [key: string]: unknown;
}

/** App-level state (last screen, profile step) mirrored inside the encrypted payload. */
export type ProfileAppState = Record<string, unknown>;

/** Strategies/scenarios/SOA case studies/service plans mirrored inside the encrypted payload. */
export interface ProfileAppData {
  coupleValidation?: Record<string, unknown>;
  [key: string]: unknown;
}

/** Profile list-row summary (server/src/shared/clientProfileRepo.ts's ProfileSummary). */
/**
 * Where a client file has actually got to — all of it derived from unencrypted metadata server-side
 * (see clientProfileRepo.listProfiles). The Partner Portal turns this into the pipeline column the
 * client sits in; before it existed the portal had nothing to go on and showed every real client as
 * "In Progress — 50%".
 */
export interface ClientProfileWorkflow {
  /** 0-100, or null on a file not written since the progress columns landed — UNKNOWN, not zero. */
  progressPct: number | null;
  completedSteps: number | null;
  totalSteps: number | null;
  /** First time the fact-find was complete; null if it never has been. */
  completedAt: string | null;
  soaCount: number;
  soaPublishedCount: number;
  latestSoaAt: string | null;
  latestPublishedAt: string | null;
  modelCount: number;
  latestModelAt: string | null;
  /** The most recent client invite for this file, so a reminder can resend it. */
  inviteId: string | null;
  /** When a client invite was last sent for this file; null if created in person. */
  inviteSentAt: string | null;
  /** When the client accepted it. Sent-but-not-accepted is what "Invite Sent" means. */
  inviteAcceptedAt: string | null;
  /** Withdrawn or lapsed — no longer outstanding. */
  inviteClosed: boolean;
}

export interface ClientProfileSummary {
  /** Absent for a single-file caller: the server withholds it, and /rest/profiles/mine needs none. */
  id?: string;
  tenantId: string;
  displayName: string;
  schemaVersion: number;
  createdAt: string;
  updatedAt: string;
  /** The firm member who created this client file — what the portal scopes an adviser's list on. */
  createdBy?: string | null;
  /**
   * The member RESPONSIBLE for this client, which is not always who created it — support can
   * prepare a file for an adviser. This is whose book it sits on.
   */
  adviserUserId?: string | null;
  /** 'restricted' = the responsible adviser and the principal only; support never sees it at all. */
  visibility?: 'firm' | 'restricted';
  /** Absent from older servers; the portal degrades to "unknown progress" without it. */
  workflow?: ClientProfileWorkflow;
}

export interface LoadedClientProfile {
  /** Null when the file was loaded id-free through /rest/profiles/mine. */
  profileId: string | null;
  factFind: DraftFactFind;
  appState: ProfileAppState | null;
  appData: ProfileAppData | null;
}

function getApiBaseUrl(): string {
  // One source for this — see services/apiBase.ts for why eight copies was a problem.
  return apiBaseUrl();
}

async function authHeaders(): Promise<{ Authorization: string; 'Content-Type': string }> {
  const token = await getAccessToken();
  if (!token) throw new Error('Not signed in');
  return { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };
}

/** Split the app's factFind into the identity segment + a de-identified fact-find. */
export function splitFactFind(factFind: DraftFactFind | null | undefined): { identity: ClientProfileIdentity; factFind: DraftFactFind } {
  const fd = structuredClone(factFind || {});
  const client = fd.client || {};
  const partner = fd.partner || {};

  const fullName = [client.firstName, client.lastName].filter(Boolean).join(' ').trim();
  const identity = {
    fullName: fullName || 'Client',
    dateOfBirth: client.dob || undefined,
    email: client.email || undefined,
    phone: client.preferredPhone || undefined,
    partnerFullName: [partner.firstName, partner.lastName].filter(Boolean).join(' ').trim() || undefined,
    partnerDateOfBirth: partner.dob || undefined,
    completerFirstName: fd.completerFirstName || undefined,
    completerLastName: fd.completerLastName || undefined,
  };

  // Blank every identifying field in the stored fact-find (merged back on load).
  if (fd.client) {
    fd.client = { ...fd.client, firstName: '', lastName: '', email: '', preferredPhone: '', dob: '' };
  }
  if (fd.partner) {
    fd.partner = { ...fd.partner, firstName: '', lastName: '', dob: '' };
  }
  fd.completerFirstName = '';
  fd.completerLastName = '';

  normalizeNumericFactFindFields(fd);

  return { identity, factFind: fd };
}

/** Reverse of splitFactFind: put the identity back into a loaded fact-find. */
export function mergeFactFind(factFind: DraftFactFind | null | undefined, identity: ClientProfileIdentity | null | undefined): DraftFactFind {
  const fd = structuredClone(factFind || {});
  const id: Partial<ClientProfileIdentity> = identity || {};
  const [firstName = '', ...rest] = String(id.fullName || '').split(' ');
  const [partnerFirst = '', ...partnerRest] = String(id.partnerFullName || '').split(' ');

  fd.client = {
    ...(fd.client || {}),
    firstName,
    lastName: rest.join(' '),
    email: id.email || '',
    preferredPhone: id.phone || '',
    dob: id.dateOfBirth || '',
  };
  if (id.partnerFullName || fd.partner) {
    fd.partner = {
      ...(fd.partner || {}),
      firstName: partnerFirst,
      lastName: partnerRest.join(' '),
      dob: id.partnerDateOfBirth || '',
    };
  }
  if (id.completerFirstName) fd.completerFirstName = id.completerFirstName;
  if (id.completerLastName) fd.completerLastName = id.completerLastName;
  return fd;
}

const PROFILE_ID_KEY = 'awiseAccountProfileId';

/**
 * Session-storage keys that hold real client work (saved strategies, scenarios, SOA case
 * studies, service plans). They're captured into the encrypted account payload on save and
 * written back on resume, so nothing done in the app is lost when the browser closes.
 * (Transient navigation flags like the reopen/restore keys are deliberately excluded.)
 */
const SESSION_MIRROR_KEYS = [
  'careCompanionStrategies',
  'careCompanionScenarios',
  'careCompanionCaseStudies',
  'careCompanionCurrentServicePlan',
  'careCompanionSelectedAgedCareStrategy',
  'careCompanionOptimizedFinancials',
];

/**
 * Session keys whose contents app data ALREADY carries (`savedStrategies`, `savedCaseStudies`). They
 * are not mirrored a second time: the mirror used to hold a full copy of every saved strategy and
 * every SOA draft beside the arrays themselves, so a file with a few strategies and drafts passed the
 * server's body limit and every save failed with 413 (owner-reported, 9 Oct 2026). On load they are
 * rebuilt from the arrays instead.
 */
const MIRROR_KEYS_HELD_IN_APP_DATA: Record<string, 'savedStrategies' | 'savedCaseStudies'> = {
  careCompanionStrategies: 'savedStrategies',
  careCompanionCaseStudies: 'savedCaseStudies',
};

export function captureSessionMirror(): Record<string, string> {
  const mirror: Record<string, string> = {};
  for (const key of SESSION_MIRROR_KEYS) {
    if (key in MIRROR_KEYS_HELD_IN_APP_DATA) continue;
    const value = sessionStorage.getItem(key);
    if (value !== null) mirror[key] = value;
  }
  return mirror;
}

/**
 * Put the session back as it was saved: the mirrored keys as stored, and the keys app data holds
 * rebuilt from its arrays (a file saved before 9 Oct 2026 still carries them in the mirror, which
 * wins where present).
 */
export function restoreSessionMirror(appData: ProfileAppData | null | undefined): void {
  if (!appData) return;
  const mirror = (appData.sessionMirror && typeof appData.sessionMirror === 'object' ? appData.sessionMirror : {}) as Record<string, unknown>;
  for (const key of SESSION_MIRROR_KEYS) {
    if (typeof mirror[key] === 'string') {
      sessionStorage.setItem(key, mirror[key] as string);
      continue;
    }
    const held = MIRROR_KEYS_HELD_IN_APP_DATA[key];
    if (held && Array.isArray(appData[held])) sessionStorage.setItem(key, JSON.stringify(appData[held]));
  }
}

/** Sign-out privacy: remove the client's work from this browser. */
export function clearSessionMirror(): void {
  for (const key of SESSION_MIRROR_KEYS) sessionStorage.removeItem(key);
}

export function getStoredProfileId(): string | null {
  return sessionStorage.getItem(PROFILE_ID_KEY) || null;
}

export function setStoredProfileId(profileId: string | null): void {
  if (profileId) sessionStorage.setItem(PROFILE_ID_KEY, profileId);
  else sessionStorage.removeItem(PROFILE_ID_KEY);
}

/** Clones appData and normalizes its numeric-ish fields (coupleValidation) the same way
 *  splitFactFind does for factFind — appData carries live React state, so it's cloned first
 *  rather than mutated in place. */
export function normalizeAppData(appData: ProfileAppData | null | undefined): ProfileAppData {
  const cloned = structuredClone(appData || {});
  normalizeNumericFieldsOn(cloned.coupleValidation, NUMERIC_FIELDS.coupleValidationItem);
  return cloned;
}

interface SaveClientProfileParams {
  factFind: DraftFactFind;
  profileId?: string | null;
  appState?: ProfileAppState | null;
  appData?: ProfileAppData | null;
  tenantId?: string | null;
}

/*
 * ONE WRITER AT A TIME (owner, 23 Oct 2026: a browser left open on a file kept saving its older copy over
 * newer work, and two village plans were lost). Each file's version — its `updatedAt` from the last load
 * or save — is remembered here and quoted with the next save; the server refuses a save whose file has
 * moved on since (409 PROFILE_CHANGED) instead of overwriting it. Keyed by profile id; "mine" for the
 * signed-in user's own file, which has no id on the wire.
 */
const knownVersions = new Map<string, string>();
const versionKey = (profileId: string | null) => profileId ?? 'mine';
function rememberVersion(profileId: string | null, updatedAt: unknown): void {
  if (typeof updatedAt === 'string' && updatedAt) knownVersions.set(versionKey(profileId), updatedAt);
}
const expectedVersion = (profileId: string | null) => {
  const v = knownVersions.get(versionKey(profileId));
  return v ? { expectedUpdatedAt: v } : {};
};
/** True when a save was refused because the file was saved elsewhere since it was opened here. */
export function isProfileChangedError(err: unknown): boolean {
  return axios.isAxiosError(err) && err.response?.status === 409 && (err.response?.data as { error?: { code?: string } } | undefined)?.error?.code === 'PROFILE_CHANGED';
}

/**
 * Save the current fact-find. Pass profileId to update; omit to create. Returns { profileId }.
 * `appState` (last screen, profile step) and `appData` (strategies, scenarios, SOA case studies,
 * service plans) ride along inside the encrypted payload so signing in later restores everything.
 */
export async function saveClientProfile({ factFind: wholeFactFind, profileId = null, appState = null, appData = null, tenantId = null }: SaveClientProfileParams): Promise<{ profileId: string | null }> {
  const headers = await authHeaders();
  const { identity, factFind } = splitFactFind(wholeFactFind);
  if (appState) factFind.__appState = appState;
  if (appData) factFind.__appData = normalizeAppData(appData);
  if (profileId) {
    const res = await axios.put(
      `${getApiBaseUrl()}/rest/profiles/${profileId}`,
      { identity, factFind, schemaVersion: 1, ...expectedVersion(profileId) },
      { headers, timeout: 30_000 },
    );
    rememberVersion(profileId, res.data?.updatedAt);
    return { profileId };
  }
  const res = await axios.post(
    `${getApiBaseUrl()}/rest/profiles`,
    // tenantId says which workspace the file belongs to (an adviser belongs to their firm AND
    // may have a personal plan — new client files must land in the firm).
    { identity, factFind, schemaVersion: 1, ...(tenantId ? { tenantId } : {}) },
    { headers, timeout: 30_000 },
  );
  // Withheld when the new file is the caller's only one — they reach it through /rest/profiles/mine.
  return { profileId: res.data.profileId ?? null };
}

/**
 * Background auto-save of the signed-in user's OWN plan (never an adviser's client file — see
 * useAutoSave, which routes those through saveClientProfile with an explicit id).
 *
 * Uses the id-free PUT only when the server has SAID this caller is single-file, and the remembered
 * id otherwise. It briefly tried the id-free route first and fell back on 409, which worked and was
 * wrong: a browser logs every non-2xx response as a console error, so a routine control-flow 409 put a
 * red line in the user's devtools on every autosave and broke any check for a clean console.
 *
 * `soleProfileMode` is refreshed by listClientProfiles(), which every load path runs before the first
 * save. A create whose response withheld the id corrects it too, so the flag is never stale for long.
 */
export async function autoSaveClientProfile({ factFind, appState, appData }: Pick<SaveClientProfileParams, 'factFind' | 'appState' | 'appData'>): Promise<{ profileId: string | null }> {
  if (soleProfileMode) {
    const headers = await authHeaders();
    const { identity, factFind: split } = splitFactFind(factFind);
    if (appState) split.__appState = appState;
    if (appData) split.__appData = normalizeAppData(appData);
    const res = await axios.put(`${getApiBaseUrl()}/rest/profiles/mine`, { identity, factFind: split, schemaVersion: 1, ...expectedVersion(null) }, { headers, timeout: 30_000 });
    rememberVersion(null, res.data?.updatedAt);
    return { profileId: null };
  }
  const saved = await saveClientProfile({ factFind, profileId: getStoredProfileId(), appState, appData });
  // A withheld id means the file just created is the caller's only one: they are single-file from here,
  // and there is no id to remember.
  if (saved.profileId === null) soleProfileMode = true;
  setStoredProfileId(saved.profileId);
  return saved;
}

/**
 * Whether the server last reported this caller as resolving to exactly ONE file.
 *
 * In that mode the server withholds profile ids entirely and serves the file through
 * /rest/profiles/mine, so there is no id for this module to hold, store or send. Refreshed by every
 * listClientProfiles() call — which every load path runs first — rather than assumed from the user's
 * role, because the server decides it from what the caller can actually see.
 */
let soleProfileMode = false;

/** List the profiles this user can see (already tenant-scoped by the server). */
export async function listClientProfiles(): Promise<ClientProfileSummary[]> {
  const headers = await authHeaders();
  const res = await axios.get(`${getApiBaseUrl()}/rest/profiles`, { headers, timeout: 30_000 });
  soleProfileMode = res.data.soleProfile === true;
  return res.data.profiles || [];
}

/** Load the caller's own file WITHOUT naming it — the id-free route. Null when they aren't single-file. */
export async function loadOwnClientProfile(): Promise<LoadedClientProfile | null> {
  const headers = await authHeaders();
  try {
    const res = await axios.get(`${getApiBaseUrl()}/rest/profiles/mine`, { headers, timeout: 30_000 });
    const { identity, factFind } = res.data.profile;
    rememberVersion(null, res.data.profile?.updatedAt);
    const { __appState = null, __appData = null, ...cleanFactFind } = factFind || {};
    // Nothing to remember: there is no id, which is the point.
    setStoredProfileId(null);
    return { profileId: null, factFind: mergeFactFind(cleanFactFind, identity), appState: __appState, appData: __appData };
  } catch (err) {
    if (axios.isAxiosError(err) && err.response?.status === 409) return null;
    throw err;
  }
}

/** Load one profile and reassemble it. Returns { profileId, factFind, appState, appData }.
 *  `remember=false` for adviser-opened client files, so they never masquerade as the
 *  signed-in user's own plan. */
export async function loadClientProfile(profileId: string, { remember = true }: { remember?: boolean } = {}): Promise<LoadedClientProfile> {
  const headers = await authHeaders();
  const res = await axios.get(`${getApiBaseUrl()}/rest/profiles/${profileId}`, { headers, timeout: 30_000 });
  const { identity, factFind } = res.data.profile;
  rememberVersion(profileId, res.data.profile?.updatedAt);
  const { __appState = null, __appData = null, ...cleanFactFind } = factFind || {};
  if (remember) setStoredProfileId(profileId);
  return { profileId, factFind: mergeFactFind(cleanFactFind, identity), appState: __appState, appData: __appData };
}

/** APP 12: download everything we hold about the signed-in user, as a JSON file. */
export async function exportMyData(): Promise<Record<string, unknown>> {
  const headers = await authHeaders();
  const res = await axios.get(`${getApiBaseUrl()}/rest/privacy/export`, { headers, timeout: 60_000 });
  return res.data;
}

/** APP 13: record a deletion request (fulfilled within 30 days; advice records legally retained). */
export async function requestAccountDeletion(): Promise<Record<string, unknown>> {
  const headers = await authHeaders();
  const res = await axios.post(`${getApiBaseUrl()}/rest/privacy/delete-request`, {}, { headers, timeout: 30_000 });
  return res.data;
}

/** Load the most recently updated profile, or null when the account has none yet. */
export async function loadLatestClientProfile(): Promise<LoadedClientProfile | null> {
  const profiles = await listClientProfiles();
  if (!profiles.length) return null;
  // One file: the server withheld its id and serves it through the id-free route.
  if (soleProfileMode) return loadOwnClientProfile();
  return loadClientProfile(profiles[0].id!);
}
